import React, { useState } from 'react'
import { Modal, Button, Tag, Typography, Icon, Empty } from 'antd'
import moment from 'moment'
import './VideoHistoryModal.scss'

const { Text, Title } = Typography

const CustomExpandable = ({ header, children, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded)

  return (
    <div className="custom-expandable">
      <div className="expandable-header" onClick={() => setExpanded(!expanded)}>
        {header}
        <Icon type={expanded ? 'up' : 'down'} className="expand-icon" />
      </div>
      {expanded && <div className="expandable-content">{children}</div>}
    </div>
  )
}

const VideoHistoryModal = ({ visible, onCancel, history = [], onSelect }) => {
  const [selectedVideo, setSelectedVideo] = useState(null)

  const handleCardClick = item => {
    setSelectedVideo(item)
  }

  return (
    <Modal
      maskClosable={false}
      title={
        <div className="modal-header">
          <div className="modal-header-icon">
            <Icon type="history" />
          </div>
          <div className="modal-header-text">
            <Title level={4} className="modal-title">
              Generation History
            </Title>
            <Text className="modal-subtitle">
              {history?.length || 0}{' '}
              {history?.length === 1 ? 'version' : 'versions'}
            </Text>
          </div>
        </div>
      }
      visible={visible}
      onCancel={() => {
        setSelectedVideo(null)
        onCancel()
      }}
      footer={
        <div className="modal-footer">
          <Button
            onClick={() => {
              setSelectedVideo(null)
              onCancel()
            }}
            className="close-button"
          >
            Close
          </Button>
        </div>
      }
      width={1200}
      bodyStyle={{ padding: 0 }}
      className="video-history-modal"
      centered
      destroyOnClose
    >
      <div className="modal-body-layout">
        {!history || history.length === 0 ? (
          <div className="empty-state">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div className="empty-description">
                  <Text className="empty-title">No History Available</Text>
                  <Text className="empty-subtitle">
                    Your generated videos will appear here
                  </Text>
                </div>
              }
            />
          </div>
        ) : (
          <div className="content-layout">
            {/* Left Column - Grid */}
            <div
              className={`grid-column ${selectedVideo ? 'has-selection' : ''}`}
            >
              <div className="grid-container">
                <div className="grid-wrapper">
                  {Array.isArray(history) &&
                    [...history].reverse().map((item, index) => (
                      <div
                        key={index}
                        className={`grid-card ${
                          selectedVideo?.date === item.date ? 'active' : ''
                        }`}
                        onClick={() => handleCardClick(item)}
                      >
                        <div className="thumbnail-container">
                          <video
                            className="thumbnail-video"
                            src={item.url}
                            preload="metadata"
                          />
                          <div className="thumbnail-overlay">
                            <Icon type="play-circle" className="play-icon" />
                          </div>
                        </div>
                        <div className="card-info">
                          <div className="card-date">
                            {moment(item.date).format('MMM DD, HH:mm')}
                          </div>
                          <div className="card-meta">
                            {item.hasMusic ? (
                              <Tag className="mini-tag music">
                                <Icon type="customer-service" />
                              </Tag>
                            ) : (
                              <Tag className="mini-tag base">
                                <Icon type="video-camera" />
                              </Tag>
                            )}
                            {item.musicAnalysis?.video_duration_ms && (
                              <span className="duration">
                                {Math.round(
                                  item.musicAnalysis.video_duration_ms / 1000
                                )}
                                s
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            {/* Right Column - Detail View */}
            <div className={`detail-column ${selectedVideo ? 'active' : ''}`}>
              {selectedVideo && (
                <div className="detail-container">
                  <div
                    className="detail-close-btn"
                    onClick={() => setSelectedVideo(null)}
                  >
                    <Icon type="close" />
                  </div>
                  {/* Video Player */}
                  <div className="video-player-section">
                    <video
                      key={selectedVideo.url}
                      className="detail-video-player"
                      src={selectedVideo.url}
                      controls
                      autoPlay
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  {/* Video Info */}
                  <div className="detail-info">
                    <div className="info-header">
                      <div className="header-left">
                        <div className="detail-date">
                          <Icon type="calendar" />
                          <span>
                            {moment(selectedVideo.date).format('MMMM DD, YYYY')}
                          </span>
                        </div>
                        <div className="detail-time">
                          {moment(selectedVideo.date).format('h:mm A')}
                        </div>
                      </div>
                      <div className="header-right">
                        {selectedVideo.hasMusic ? (
                          <Tag className="tag-music">
                            <Icon type="customer-service" />
                            <span>Music Enhanced</span>
                          </Tag>
                        ) : (
                          <Tag className="tag-base">
                            <Icon type="video-camera" />
                            <span>Base Video</span>
                          </Tag>
                        )}
                      </div>
                    </div>

                    {/* AI Analysis */}
                    {selectedVideo.hasMusic && selectedVideo.musicAnalysis && (
                      <div className="analysis-section">
                        <CustomExpandable
                          header={
                            <div className="expandable-header-content">
                              <Icon
                                type="bulb"
                                theme="filled"
                                className="header-icon"
                              />
                              <Text className="header-title">
                                AI Analysis & Metadata
                              </Text>
                            </div>
                          }
                          defaultExpanded={true}
                        >
                          <div className="analysis-content">
                            {/* Description */}
                            {selectedVideo.musicAnalysis.video_description && (
                              <div className="analysis-item">
                                <div className="item-label">
                                  <Icon type="file-text" />
                                  <span>Description</span>
                                </div>
                                <div className="item-value description-text">
                                  {
                                    selectedVideo.musicAnalysis
                                      .video_description
                                  }
                                </div>
                              </div>
                            )}

                            {/* Mood & Tempo */}
                            <div className="meta-grid">
                              {selectedVideo.musicAnalysis.mood && (
                                <div className="analysis-item">
                                  <div className="item-label">
                                    <Icon type="smile" />
                                    <span>Mood</span>
                                  </div>
                                  <div className="item-value">
                                    {selectedVideo.musicAnalysis.mood
                                      .split(',')
                                      .map((m, idx) => (
                                        <Tag key={idx} className="mood-tag">
                                          {m.trim()}
                                        </Tag>
                                      ))}
                                  </div>
                                </div>
                              )}
                              {selectedVideo.musicAnalysis.tempo && (
                                <div className="analysis-item">
                                  <div className="item-label">
                                    <Icon type="dashboard" />
                                    <span>Tempo</span>
                                  </div>
                                  <div className="item-value">
                                    <Tag className="tempo-tag">
                                      {selectedVideo.musicAnalysis.tempo.toUpperCase()}
                                    </Tag>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Genres */}
                            {selectedVideo.musicAnalysis.genre_suggestions && (
                              <div className="analysis-item">
                                <div className="item-label">
                                  <Icon type="sound" />
                                  <span>Genres</span>
                                </div>
                                <div className="item-value">
                                  {selectedVideo.musicAnalysis.genre_suggestions.map(
                                    (g, idx) => (
                                      <Tag key={idx} className="genre-tag">
                                        {g}
                                      </Tag>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </CustomExpandable>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default VideoHistoryModal
